// PanelThemeProvider: light by default, dark through the toggle, the viewer's choice kept in localStorage.
// MGMT-PANEL-SHELL-REGRESSIONS-01: the operating system's light/dark setting is never read. The first visit
// opens light; only the viewer's own toggle (stored under STORAGE_KEY) changes it. The light scheme is
// declared "only light" so a browser's automatic dark mode (Chrome "Auto Dark Mode for Web Contents",
// Samsung Internet, Android "dark theme for sites") cannot darken the panel on its own; the browser bar
// colour (meta theme-color) follows the scheme's ground instead of the old #000000.
// [THEME_DEFAULT] {"scheme":"light","source":"default|stored"} is logged once at load.
import React, { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { cssVarsFor, muiThemeFor, normalizeScheme, DEFAULT_SCHEME, STORAGE_KEY } from './united';

// Returns { scheme, source }: 'stored' only when the viewer's own toggle saved a valid scheme.
export function readInitialScheme() {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (raw === 'light' || raw === 'dark') return { scheme: raw, source: 'stored' };
    } catch (e) {
        // storage unavailable: the default applies
    }
    return { scheme: DEFAULT_SCHEME, source: 'default' };
}

export function readStoredScheme() {
    return readInitialScheme().scheme;
}

let defaultLogged = false;
export function _resetThemeDefaultLog() { defaultLogged = false; }
function logThemeDefaultOnce(initial) {
    if (defaultLogged) return;
    defaultLogged = true;
    console.info(`[THEME_DEFAULT] ${JSON.stringify({ scheme: initial.scheme, source: initial.source })}`);
}

function writeStoredScheme(scheme) {
    try {
        window.localStorage.setItem(STORAGE_KEY, scheme);
    } catch (e) {
        // storage unavailable (private mode, blocked): the choice lasts for this page only
    }
}

export function applySchemeToDocument(scheme) {
    const root = document.documentElement;
    Object.entries(cssVarsFor(scheme)).forEach(([name, value]) => root.style.setProperty(name, value));
    root.setAttribute('data-theme', scheme);
    // 'only light' keeps browsers' automatic dark mode off; dark is declared plainly.
    root.style.colorScheme = scheme === 'dark' ? 'dark' : 'only light';
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (themeColor) themeColor.setAttribute('content', cssVarsFor(scheme)['--bg']);
    root.style.backgroundColor = 'var(--bg)'; // replaces the pre-paint colour set in public/index.html
}

const PanelThemeContext = createContext({ scheme: DEFAULT_SCHEME, setScheme: () => {}, toggleScheme: () => {} });

export function PanelThemeProvider({ children }) {
    const [scheme, setSchemeState] = useState(() => {
        const initial = readInitialScheme();
        logThemeDefaultOnce(initial);
        return initial.scheme;
    });

    useLayoutEffect(() => { applySchemeToDocument(scheme); }, [scheme]);

    const setScheme = useCallback((next) => {
        const s = normalizeScheme(next);
        writeStoredScheme(s);
        setSchemeState(s);
    }, []);
    const toggleScheme = useCallback(() => setScheme(scheme === 'dark' ? 'light' : 'dark'), [scheme, setScheme]);

    const muiTheme = useMemo(() => muiThemeFor(scheme), [scheme]);
    const value = useMemo(() => ({ scheme, setScheme, toggleScheme }), [scheme, setScheme, toggleScheme]);

    return (
        <PanelThemeContext.Provider value={value}>
            <ThemeProvider theme={muiTheme}>
                <CssBaseline />
                {children}
            </ThemeProvider>
        </PanelThemeContext.Provider>
    );
}

export const usePanelTheme = () => useContext(PanelThemeContext);
